
/**
 * Duration calculation utilities for various date operations
 */

import dayjs from 'dayjs';
import duration from 'dayjs/plugin/duration';
import { getMinutesTimestamp } from './time';

// Initialize duration plugin
dayjs.extend(duration);

/**
 * Calculates and formats the duration between two dates
 * 
 * @param startDate The start date as a dayjs object
 * @param endDate The end date as a dayjs object
 * @returns A formatted duration string
 */
export function formatDuration(startDate: dayjs.Dayjs | null, endDate: dayjs.Dayjs | null): string {
  if (!startDate) return 'No start time';
  if (!endDate) return 'No end time';
  
  const durationMs = endDate.diff(startDate);
  
  // Handle edge case: identical timestamps
  if (durationMs === 0) {
    return '< 1 minute';
  }
  
  // Handle edge case: negative duration
  if (durationMs < 0) {
    console.warn(`Negative duration detected: ${startDate.format()} to ${endDate.format()}`);
    return 'Invalid duration';
  }
  
  const hours = Math.floor(durationMs / (1000 * 60 * 60));
  const minutes = Math.floor((durationMs % (1000 * 60 * 60)) / (1000 * 60));
  
  if (hours > 0) {
    return minutes > 0 ? `${hours}h ${minutes}m` : `${hours}h`;
  } else {
    return `${minutes}m`;
  }
}

/**
 * Calculates and formats the duration between two date strings
 * 
 * @param startDateString The start date as a string
 * @param endDateString The end date as a string
 * @returns A formatted duration string
 */
export function formatDurationFromStrings(startDateString: string | null | undefined, endDateString: string | null | undefined): string {
  const startDate = dayjs(startDateString);
  const endDate = endDateString ? dayjs(endDateString) : (startDate ? startDate.add(1, 'minute') : null);
  
  if (!startDate.isValid()) return 'Invalid start time';
  if (!endDate || !endDate.isValid()) return 'Invalid end time';
  
  return formatDuration(startDate, endDate);
}

/**
 * Calculate duration in minutes between two timestamps
 * 
 * @param startTimestamp Start timestamp in minutes or date string
 * @param endTimestamp End timestamp in minutes or date string
 * @returns Duration in minutes, or null if calculation fails
 */
export function calculateDurationMinutes(
  startTimestamp: number | string | null | undefined, 
  endTimestamp: number | string | null | undefined
): number | null {
  // Handle null inputs
  if (startTimestamp === null || startTimestamp === undefined || 
      endTimestamp === null || endTimestamp === undefined) {
    return null;
  }
  
  // Convert string dates to minute timestamps if needed
  const startMinutes = typeof startTimestamp === 'string' 
    ? getMinutesTimestamp(startTimestamp) 
    : startTimestamp;
    
  const endMinutes = typeof endTimestamp === 'string'
    ? getMinutesTimestamp(endTimestamp)
    : endTimestamp;
  
  // Ensure both timestamps are valid
  if (startMinutes === null || endMinutes === null) {
    return null;
  }
  
  // Calculate duration
  const duration = endMinutes - startMinutes;
  
  // Validate duration is not negative
  if (duration < 0) {
    console.warn('Calculated negative duration, returning absolute value:', duration);
    return Math.abs(duration);
  }
  
  return duration;
}

/**
 * Gets the duration between two dates in a human-readable format
 */
export function getDuration(startDate: Date, endDate: Date): string {
  const start = dayjs(startDate);
  const end = dayjs(endDate);
  const diffMinutes = end.diff(start, 'minute');
  
  const hours = Math.floor(diffMinutes / 60);
  const minutes = diffMinutes % 60;
  
  if (hours === 0) {
    return `${minutes}m`;
  }
  
  return `${hours}h ${minutes}m`;
}
